import React, { useState, useEffect, useMemo } from 'react';
import { View, StyleSheet, ScrollView, Alert, TouchableOpacity, Image } from 'react-native';
import { Text, TextInput, Button, Divider, HelperText, ActivityIndicator, Menu, Portal, Dialog, IconButton } from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import productosApi from '../../services/productosApi';
import apiService from '../../services/api';
import { UNIDAD_ENTRY, buildEntryOptions, convertDisplayed, toBaseValues } from '../../utils/presentacionEntry';
import { markInventoryDirty } from '../../utils/inventoryRefresh';

const NuevoProductoScreen = () => {
  const navigation = useNavigation();
  const [loading, setLoading] = useState(false);
  const [loadingCategorias, setLoadingCategorias] = useState(true);
  const [categorias, setCategorias] = useState([]);
  const [categoriaMenuVisible, setCategoriaMenuVisible] = useState(false);
  const [selectedCategoria, setSelectedCategoria] = useState(null);

  const [formData, setFormData] = useState({
    nombre: '',
    codigo: '',
    descripcion: '',
    codigoBarras: '',
    marca: '',
    precioCompra: '',
    precioVenta: '',
    stock: '0',
    stockMinimo: '0',
    imagen: '',
    notas: ''
  });
  const [errors, setErrors] = useState({});

  // ===== Presentaciones (configuradas localmente antes de crear el producto) =====
  const [catalogoPresentaciones, setCatalogoPresentaciones] = useState([]);
  const [presentacionesLocal, setPresentacionesLocal] = useState([]);
  const [showAddPresentacion, setShowAddPresentacion] = useState(false);
  const [showCatalogoMenu, setShowCatalogoMenu] = useState(false);
  const [selectedPresentacion, setSelectedPresentacion] = useState(null);
  const [factorInput, setFactorInput] = useState('');
  // Precio de venta propio de la presentación (opcional; vacío = proporcional)
  const [precioInput, setPrecioInput] = useState('');
  const [editIndex, setEditIndex] = useState(null);

  // ===== Captura de Precio/Stock por presentación =====
  const [entryId, setEntryId] = useState(null); // null = Unidad (factor 1)
  const [showEntryMenu, setShowEntryMenu] = useState(false);
  const entryOptions = useMemo(() => buildEntryOptions(presentacionesLocal), [presentacionesLocal]);
  const entry = entryOptions.find((o) => o.presentacion_id === entryId) || UNIDAD_ENTRY;

  const handleSelectEntry = (opcion) => {
    const convertidos = convertDisplayed(
      { stock: formData.stock, precioVenta: formData.precioVenta, precioCompra: formData.precioCompra },
      entry.factor,
      opcion.factor
    );
    setFormData((prev) => ({ ...prev, ...convertidos }));
    setEntryId(opcion.presentacion_id);
    setShowEntryMenu(false);
  };

  const presentacionesDisponibles = catalogoPresentaciones.filter(
    (c) => !presentacionesLocal.some((pp, i) => pp.presentacion_id === c.id && i !== editIndex)
  );

  const abrirAgregarPresentacion = () => {
    setEditIndex(null);
    setSelectedPresentacion(null);
    setFactorInput('');
    setPrecioInput('');
    setShowAddPresentacion(true);
  };

  const abrirEditarPresentacion = (index) => {
    const pp = presentacionesLocal[index];
    setEditIndex(index);
    setSelectedPresentacion({ id: pp.presentacion_id, nombre: pp.nombre, abreviatura: pp.abreviatura });
    setFactorInput(String(pp.factor_conversion));
    setPrecioInput(pp.precio_venta != null && pp.precio_venta !== '' ? String(pp.precio_venta) : '');
    setShowAddPresentacion(true);
  };

  const guardarPresentacionLocal = () => {
    const factor = parseFloat(factorInput);
    if (!selectedPresentacion) {
      Alert.alert('Falta dato', 'Selecciona la presentación');
      return;
    }
    if (isNaN(factor) || factor <= 0) {
      Alert.alert('Falta dato', 'El factor debe ser mayor a 0 (ej: 12, 24, 50)');
      return;
    }
    const precioVenta = precioInput.trim() === '' ? null : (parseFloat(precioInput) || 0);
    const nueva = {
      presentacion_id: selectedPresentacion.id,
      nombre: selectedPresentacion.nombre,
      abreviatura: selectedPresentacion.abreviatura,
      factor_conversion: factor,
      precio_venta: precioVenta,
    };
    setPresentacionesLocal((prev) => {
      if (editIndex !== null) {
        const copia = [...prev];
        copia[editIndex] = nueva;
        return copia;
      }
      return [...prev, nueva];
    });
    setShowAddPresentacion(false);
  };

  const eliminarPresentacionLocal = (index) => {
    const pp = presentacionesLocal[index];
    // Si estábamos capturando en esa presentación, volver a Unidad.
    if (pp.presentacion_id === entryId) setEntryId(null);
    setPresentacionesLocal((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    loadCategorias();
    loadCatalogoPresentaciones();
  }, []);

  const loadCatalogoPresentaciones = async () => {
    try {
      const data = await apiService.getPresentaciones();
      setCatalogoPresentaciones(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error al cargar catálogo de presentaciones:', error);
    }
  };

  const loadCategorias = async () => {
    try {
      setLoadingCategorias(true);
      const response = await productosApi.getCategorias();
      setCategorias(response);
    } catch (error) {
      console.error('Error al cargar categorías:', error);
      Alert.alert('Error', 'No se pudieron cargar las categorías');
    } finally {
      setLoadingCategorias(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es obligatorio';
    if (!formData.codigo.trim()) newErrors.codigo = 'El código es obligatorio';
    if (!selectedCategoria) newErrors.categoria = 'La categoría es obligatoria';

    const precioVenta = parseFloat(formData.precioVenta);
    if (!formData.precioVenta || isNaN(precioVenta) || precioVenta <= 0) {
      newErrors.precioVenta = 'El precio de venta debe ser mayor a 0';
    }

    const precioCompra = parseFloat(formData.precioCompra);
    if (formData.precioCompra && (isNaN(precioCompra) || precioCompra < 0)) {
      newErrors.precioCompra = 'El costo debe ser un número mayor o igual a 0';
    }

    const stock = parseInt(formData.stock);
    if (isNaN(stock) || stock < 0) {
      newErrors.stock = 'El stock debe ser un número entero mayor o igual a 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const pickImage = async () => {
    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert('Permiso denegado', 'Se necesita permiso para acceder a la galería de imágenes');
        return;
      }
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.5,
        base64: true,
      });
      if (!result.canceled && result.assets && result.assets.length > 0) {
        const asset = result.assets[0];
        handleChange('imagen', `data:image/jpeg;base64,${asset.base64}`);
      }
    } catch (error) {
      console.error('Error al seleccionar imagen:', error);
      Alert.alert('Error', 'No se pudo seleccionar la imagen');
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    try {
      setLoading(true);
      // Convertir Precio/Stock capturados en la presentación elegida a unidades base.
      const base = toBaseValues(
        { stock: formData.stock, precioVenta: formData.precioVenta, precioCompra: formData.precioCompra },
        entry.factor
      );
      const productoData = {
        nombre: formData.nombre,
        codigo: formData.codigo,
        descripcion: formData.descripcion || null,
        codigo_barras: formData.codigoBarras || null,
        marca: formData.marca || null,
        precio_compra: base.precio_compra,
        precio_venta: base.precio_venta,
        stock: base.stock,
        stock_minimo: parseInt(formData.stockMinimo) || 0,
        categoria_id: selectedCategoria,
        imagen: formData.imagen || null,
        notas: formData.notas || null,
      };

      const productoCreado = await productosApi.createProducto(productoData);

      // Persistir las presentaciones configuradas para el nuevo producto.
      const nuevoId = productoCreado?.id;
      if (nuevoId && presentacionesLocal.length > 0) {
        for (const pp of presentacionesLocal) {
          try {
            await apiService.addPresentacionProducto(nuevoId, {
              presentacion_id: pp.presentacion_id,
              factor_conversion: pp.factor_conversion,
              precio_venta: pp.precio_venta,
            });
          } catch (e) {
            console.error('Error al guardar presentación del producto nuevo:', e);
          }
        }
      }

      setLoading(false);
      markInventoryDirty(); // que el listado se recargue al volver
      Alert.alert('Éxito', 'Producto guardado correctamente', [
        { text: 'OK', onPress: () => navigation.goBack() }
      ]);
    } catch (error) {
      console.error('Error al guardar producto:', error);
      setLoading(false);
      Alert.alert('Error', 'No se pudo guardar el producto: ' + (error.message || 'Error desconocido'));
    }
  };

  if (loading) {
    return (
      <View style={styles.loaderContainer}>
        <ActivityIndicator size="large" color="#0066cc" />
        <Text style={styles.loaderText}>Guardando producto...</Text>
      </View>
    );
  }

  if (loadingCategorias) {
    return (
      <View style={styles.loaderContainer}>
        <ActivityIndicator size="large" color="#0066cc" />
        <Text style={styles.loaderText}>Cargando categorías...</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      <Text style={styles.title}>Nuevo Producto</Text>

      <View style={styles.formSection}>
        <Text style={styles.sectionTitle}>Información General</Text>

        <TextInput
          label="Nombre del producto *"
          value={formData.nombre}
          onChangeText={(text) => handleChange('nombre', text)}
          style={styles.input}
          error={!!errors.nombre}
        />
        {errors.nombre && <HelperText type="error">{errors.nombre}</HelperText>}

        <TextInput
          label="Código *"
          value={formData.codigo}
          onChangeText={(text) => handleChange('codigo', text)}
          style={styles.input}
          error={!!errors.codigo}
        />
        {errors.codigo && <HelperText type="error">{errors.codigo}</HelperText>}

        <TextInput
          label="Código de barras"
          value={formData.codigoBarras}
          onChangeText={(text) => handleChange('codigoBarras', text)}
          style={styles.input}
        />

        <TextInput
          label="Marca"
          value={formData.marca}
          onChangeText={(text) => handleChange('marca', text)}
          style={styles.input}
          placeholder="Ej: MK, Bayer, Genérico..."
        />

        <Text style={styles.inputLabel}>Categoría *</Text>
        <Menu
          visible={categoriaMenuVisible}
          onDismiss={() => setCategoriaMenuVisible(false)}
          anchor={
            <TouchableOpacity
              style={[styles.menuButton, errors.categoria && styles.inputError]}
              onPress={() => setCategoriaMenuVisible(true)}
            >
              <Text>
                {selectedCategoria
                  ? categorias.find(c => c.id === selectedCategoria)?.nombre || 'Seleccionar categoría'
                  : 'Seleccionar categoría'}
              </Text>
              <Ionicons name="chevron-down" size={20} color="#666" />
            </TouchableOpacity>
          }
        >
          {categorias.map((categoria) => (
            <Menu.Item
              key={categoria.id}
              title={categoria.nombre}
              onPress={() => {
                setSelectedCategoria(categoria.id);
                setCategoriaMenuVisible(false);
              }}
            />
          ))}
        </Menu>
        {errors.categoria && <HelperText type="error">{errors.categoria}</HelperText>}

        <TextInput
          label="Descripción"
          value={formData.descripcion}
          onChangeText={(text) => handleChange('descripcion', text)}
          style={styles.input}
          multiline
          numberOfLines={3}
        />

        <Text style={styles.inputLabel}>Imagen del producto</Text>
        <View style={styles.imageContainer}>
          {formData.imagen ? (
            <Image source={{ uri: formData.imagen }} style={styles.productImage} resizeMode="contain" />
          ) : (
            <View style={styles.imagePlaceholder}>
              <Ionicons name="image-outline" size={50} color="#ccc" />
              <Text style={styles.placeholderText}>Sin imagen</Text>
            </View>
          )}
          <Button mode="outlined" onPress={pickImage} style={styles.imageButton} icon="camera">
            {formData.imagen ? 'Cambiar imagen' : 'Seleccionar imagen'}
          </Button>
        </View>
      </View>

      <Divider style={styles.divider} />

      <View style={styles.formSection}>
        <Text style={styles.sectionTitle}>Precios e Inventario</Text>

        <Text style={styles.inputLabel}>Ingresar precio y stock por</Text>
        <Menu
          visible={showEntryMenu}
          onDismiss={() => setShowEntryMenu(false)}
          anchor={
            <TouchableOpacity style={styles.menuButton} onPress={() => setShowEntryMenu(true)}>
              <Text>
                {entry.nombre}{entry.factor !== 1 ? ` (1 = ${entry.factor} unidades)` : ''}
              </Text>
              <Ionicons name="chevron-down" size={20} color="#666" />
            </TouchableOpacity>
          }
        >
          {entryOptions.map((o) => (
            <Menu.Item
              key={o.presentacion_id == null ? 'unidad' : o.presentacion_id}
              title={o.factor !== 1 ? `${o.nombre} (1 = ${o.factor} unidades)` : o.nombre}
              onPress={() => handleSelectEntry(o)}
            />
          ))}
        </Menu>
        <HelperText type="info" visible>
          Escribe los valores en {entry.nombre.toLowerCase()}; se guardan convertidos a unidades.
        </HelperText>

        <TextInput
          label={`Precio de venta (L.)${entry.factor !== 1 ? ` por ${entry.nombre.toLowerCase()}` : ''} *`}
          value={formData.precioVenta}
          onChangeText={(text) => handleChange('precioVenta', text)}
          style={styles.input}
          keyboardType="numeric"
          error={!!errors.precioVenta}
        />
        {entry.factor !== 1 && !!formData.precioVenta && (
          <HelperText type="info" visible>
            = L. {((parseFloat(formData.precioVenta) || 0) / entry.factor).toFixed(2)} por unidad
          </HelperText>
        )}
        {errors.precioVenta && <HelperText type="error">{errors.precioVenta}</HelperText>}

        <TextInput
          label={`Costo (L.)${entry.factor !== 1 ? ` por ${entry.nombre.toLowerCase()}` : ''}`}
          value={formData.precioCompra}
          onChangeText={(text) => handleChange('precioCompra', text)}
          style={styles.input}
          keyboardType="numeric"
          error={!!errors.precioCompra}
        />
        {entry.factor !== 1 && !!formData.precioCompra && (
          <HelperText type="info" visible>
            = L. {((parseFloat(formData.precioCompra) || 0) / entry.factor).toFixed(2)} por unidad
          </HelperText>
        )}
        {errors.precioCompra && <HelperText type="error">{errors.precioCompra}</HelperText>}

        <TextInput
          label={`Stock inicial${entry.factor !== 1 ? ` (en ${entry.nombre.toLowerCase()})` : ''}`}
          value={formData.stock}
          onChangeText={(text) => handleChange('stock', text)}
          style={styles.input}
          keyboardType="numeric"
          error={!!errors.stock}
        />
        {entry.factor !== 1 && !!formData.stock && (
          <HelperText type="info" visible>
            = {Math.round((parseFloat(formData.stock) || 0) * entry.factor)} unidades
          </HelperText>
        )}
        {errors.stock && <HelperText type="error">{errors.stock}</HelperText>}

        <TextInput
          label="Stock mínimo (en unidades)"
          value={formData.stockMinimo}
          onChangeText={(text) => handleChange('stockMinimo', text)}
          style={styles.input}
          keyboardType="numeric"
        />
      </View>

      <View style={styles.formSection}>
        <View style={styles.presentacionesHeader}>
          <Text style={styles.sectionTitle}>Presentaciones</Text>
          <Button mode="contained-tonal" compact icon="plus" onPress={abrirAgregarPresentacion}>
            Agregar
          </Button>
        </View>
        <Text style={styles.presentacionesHint}>
          Ej: 1 Caja = 50 unidades, 1 Docena = 12. Al vender, el stock descuenta unidades reales.
        </Text>

        {presentacionesLocal.length === 0 ? (
          <Text style={styles.presentacionesEmpty}>
            Sin presentaciones. Las ventas se harán por unidad (factor 1).
          </Text>
        ) : (
          presentacionesLocal.map((pp, index) => (
            <View key={`${pp.presentacion_id}-${index}`} style={styles.presentacionRow}>
              <View style={{ flex: 1 }}>
                <Text style={styles.presentacionNombre}>{pp.nombre}</Text>
                <Text style={styles.presentacionFactor}>
                  1 {pp.nombre} = {Number(pp.factor_conversion)} unidades
                  {pp.precio_venta != null && pp.precio_venta !== ''
                    ? `  ·  L. ${Number(pp.precio_venta).toFixed(2)}`
                    : ''}
                </Text>
              </View>
              <IconButton icon="pencil" size={20} onPress={() => abrirEditarPresentacion(index)} />
              <IconButton icon="delete" size={20} iconColor="#DC3545" onPress={() => eliminarPresentacionLocal(index)} />
            </View>
          ))
        )}
      </View>

      <View style={styles.buttonContainer}>
        <Button mode="contained" onPress={handleSubmit} style={styles.submitButton} loading={loading}>
          Guardar Producto
        </Button>
        <Button mode="outlined" onPress={() => navigation.goBack()} style={styles.cancelButton}>
          Cancelar
        </Button>
      </View>

      <Portal>
        <Dialog visible={showAddPresentacion} onDismiss={() => setShowAddPresentacion(false)}>
          <Dialog.Title>{editIndex !== null ? 'Editar presentación' : 'Agregar presentación'}</Dialog.Title>
          <Dialog.Content>
            <Text style={styles.inputLabel}>Presentación</Text>
            <Menu
              visible={showCatalogoMenu}
              onDismiss={() => setShowCatalogoMenu(false)}
              anchor={
                <TouchableOpacity style={styles.menuButton} onPress={() => setShowCatalogoMenu(true)}>
                  <Text style={!selectedPresentacion ? { color: '#999' } : undefined} numberOfLines={1}>
                    {selectedPresentacion ? selectedPresentacion.nombre : 'Seleccionar...'}
                  </Text>
                  <Ionicons name="chevron-down" size={22} color="#666" />
                </TouchableOpacity>
              }
            >
              {presentacionesDisponibles.length === 0 ? (
                <Menu.Item title="No quedan presentaciones por asignar" disabled />
              ) : (
                presentacionesDisponibles.map((c) => (
                  <Menu.Item
                    key={c.id}
                    title={c.nombre}
                    onPress={() => {
                      setSelectedPresentacion(c);
                      setShowCatalogoMenu(false);
                    }}
                  />
                ))
              )}
            </Menu>

            <Text style={[styles.inputLabel, { marginTop: 12 }]}>Equivalencia en unidades (ej: 12, 24, 50)</Text>
            <TextInput
              value={factorInput}
              onChangeText={setFactorInput}
              keyboardType="numeric"
              placeholder="Cuántas unidades equivalen"
              mode="outlined"
            />

            <Text style={[styles.inputLabel, { marginTop: 12 }]}>Precio de venta de esta presentación (opcional)</Text>
            <TextInput
              value={precioInput}
              onChangeText={(t) => setPrecioInput(t.replace(/[^0-9.]/g, ''))}
              keyboardType="decimal-pad"
              placeholder="Vacío = precio por unidad × equivalencia"
              mode="outlined"
              left={<TextInput.Affix text="L." />}
            />
            <Text style={styles.presentacionesHint}>
              Déjalo vacío para precio proporcional. Ponlo si la caja/blíster tiene un precio especial (ej. mayoreo).
            </Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setShowAddPresentacion(false)}>Cancelar</Button>
            <Button mode="contained" onPress={guardarPresentacionLocal}>
              {editIndex !== null ? 'Guardar' : 'Agregar'}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5', padding: 16 },
  loaderContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#f5f5f5' },
  loaderText: { marginTop: 10, fontSize: 16, color: '#0066cc' },
  title: { fontSize: 24, fontWeight: 'bold', marginBottom: 20, color: '#333' },
  formSection: { backgroundColor: '#fff', borderRadius: 8, padding: 16, marginBottom: 16, elevation: 2 },
  sectionTitle: { fontSize: 18, fontWeight: 'bold', marginBottom: 16, color: '#333' },
  input: { marginBottom: 8, backgroundColor: '#fff' },
  inputLabel: { fontSize: 12, color: '#666', marginBottom: 4 },
  menuButton: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    borderWidth: 1, borderColor: '#ddd', borderRadius: 4, padding: 10, marginBottom: 8, backgroundColor: '#fff',
  },
  inputError: { borderColor: '#B00020' },
  imageContainer: { alignItems: 'center', marginVertical: 16 },
  productImage: { width: 200, height: 200, borderRadius: 8, marginBottom: 8 },
  imagePlaceholder: {
    width: 200, height: 200, borderRadius: 8, backgroundColor: '#f0f0f0',
    justifyContent: 'center', alignItems: 'center', marginBottom: 8,
    borderWidth: 1, borderColor: '#ddd', borderStyle: 'dashed',
  },
  placeholderText: { marginTop: 8, color: '#999' },
  imageButton: { marginTop: 8, width: 200 },
  buttonContainer: { marginTop: 20, marginBottom: 40 },
  divider: { marginVertical: 8 },
  submitButton: { marginBottom: 10, paddingVertical: 6 },
  cancelButton: { backgroundColor: '#f0f0f0', paddingVertical: 6 },
  presentacionesHeader: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4,
  },
  presentacionesHint: { fontSize: 12, color: '#6c757d', marginBottom: 12 },
  presentacionesEmpty: {
    fontSize: 13, color: '#6c757d', fontStyle: 'italic',
    paddingVertical: 12, paddingHorizontal: 12,
    backgroundColor: '#f8f9fa', borderRadius: 4, marginBottom: 8,
  },
  presentacionRow: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#f8f9fa', borderRadius: 6,
    paddingHorizontal: 12, paddingVertical: 4, marginBottom: 6,
  },
  presentacionNombre: { fontSize: 15, fontWeight: '600', color: '#212529' },
  presentacionFactor: { fontSize: 12, color: '#6c757d', marginTop: 2 },
});

export default NuevoProductoScreen;
